import { MdsDropdownButton, MdsDropdownItemKind } from "@/design-system/components/dropdown";
import { MdsSpacer } from "@/design-system/components/spacer";
import { mdsColors } from "@/design-system/foundations/colors";
import { Maybe } from "@/domains/common/types";
import { ZIndex } from "@/domains/design/constants";
import { css } from "@/domains/emotion";
import { copyTextToClipboard } from "@/modules/clipboard/copyTextToClipboard";
import { toastModule } from "@/modules/toast";
import { useAppStore } from "@/store";
import { DebugOverlayViewKind } from "@/store/debug/AppStoreDebugStore";
import { CustomSyncErrorKind } from "@/store/sync/operations/errors/SyncError";
import { getOperationKindToClassMap } from "@/store/sync/operations/map";
import { SyncOperationKind } from "@/store/sync/types";
import { observer } from "mobx-react-lite";
import { clientEnvModule } from "@/modules/client-env";
import { DateTime } from "luxon";
import { useEffect, useRef, useState } from "react";
import { MDSButton, MDSButtonVariant } from "@/mds";
import { SmartViewKind } from "@/domains/smart/constants";
import { resolveSmartContextId } from "@/store/heads-up/SmartContextId";
import styled from "@emotion/styled";

const customErrorKinds: Maybe<CustomSyncErrorKind>[] = [
  undefined,
  "INVALID",
  "PERMISSION_DENIED",
  "TRANSIENT",
  "UNKNOWN",
];

// Type for performance memory API
interface PerformanceMemory {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
}

interface PerformanceWithMemory extends Performance {
  memory?: PerformanceMemory;
}

// Performance monitoring sub-component
const PerformanceMetrics = observer(function PerformanceMetrics() {
  const [fps, setFps] = useState(0);
  const [memoryMB, setMemoryMB] = useState(0);
  const [frameTime, setFrameTime] = useState({ max: 0, avg: 0 });
  const frameCountRef = useRef(0);
  const lastTimeRef = useRef(performance.now());
  const frameTimes = useRef<number[]>([]);
  const lastUpdateRef = useRef(performance.now());

  useEffect(() => {
    let animationId: number;

    const measurePerformance = (currentTime: number) => {
      // Calculate FPS
      frameCountRef.current++;
      const deltaTime = currentTime - lastTimeRef.current;

      if (deltaTime >= 1000) {
        setFps(Math.round((frameCountRef.current * 1000) / deltaTime));
        frameCountRef.current = 0;
        lastTimeRef.current = currentTime;
      }

      // Continuously track frame times in background
      if (frameTimes.current.length > 0) {
        frameTimes.current.push(currentTime);

        // Keep only last 10 frames for lag detection
        if (frameTimes.current.length > 11) {
          // 11 timestamps = 10 frame deltas
          frameTimes.current.shift();
        }
      } else {
        frameTimes.current.push(currentTime);
      }

      // Update memory usage every 5 seconds
      if (currentTime - lastUpdateRef.current >= 5000) {
        const perfWithMemory = performance as PerformanceWithMemory;
        if (perfWithMemory.memory) {
          setMemoryMB(Math.round(perfWithMemory.memory.usedJSHeapSize / 1024 / 1024));
        }
        lastUpdateRef.current = currentTime;
      }

      animationId = requestAnimationFrame(measurePerformance);
    };

    // Add click listener to analyze recent frame times
    const handleClick = () => {
      if (frameTimes.current.length > 1) {
        // Calculate frame deltas from recent history
        const deltas = [];
        for (let i = 1; i < frameTimes.current.length; i++) {
          deltas.push(frameTimes.current[i] - frameTimes.current[i - 1]);
        }

        if (deltas.length > 0) {
          const maxFrameTime = Math.max(...deltas);
          const avgFrameTime = deltas.reduce((a, b) => a + b, 0) / deltas.length;

          setFrameTime({
            max: Math.round(maxFrameTime),
            avg: Math.round(avgFrameTime),
          });
        }
      }

      // Also update memory immediately on click
      const perfWithMemory = performance as PerformanceWithMemory;
      if (perfWithMemory.memory) {
        setMemoryMB(Math.round(perfWithMemory.memory.usedJSHeapSize / 1024 / 1024));
      }
    };

    document.addEventListener("click", handleClick, true);
    animationId = requestAnimationFrame(measurePerformance);

    return () => {
      cancelAnimationFrame(animationId);
      document.removeEventListener("click", handleClick, true);
    };
  }, []);

  return (
    <div className={performanceMetricsStyles}>
      <span>FPS: {fps.toString().padStart(2, " ")}</span>
      <span>Frame: {frameTime.max}ms</span>
      {<span>Mem: {memoryMB > 0 ? `${memoryMB}MB` : `...`}</span>}
    </div>
  );
});

// Debug content sub-component
const ExpandedDebugContent = observer(function ExpandedDebugContent() {
  const { store } = useAppStore();
  const { buildInfo } = clientEnvModule.buildInfo();

  const operationKinds: Maybe<SyncOperationKind>[] = [
    undefined,
    ...(Object.keys(getOperationKindToClassMap()) as SyncOperationKind[]),
  ];

  const [actionQueueExpanded, setActionQueueExpanded] = useState(true);
  const [audioCaptureExpanded, setAudioCaptureExpanded] = useState(false);

  return (
    <div className={debugContentStyles}>
      <div className={mockApiErrorRowStyles}>
        <MdsSpacer />
        <MdsDropdownButton
          placement="below-right-alignment"
          label={store.debug.operationsSubmitApiMockErrorKind ?? "disabled"}
          contentList={{
            items: customErrorKinds.map(kind => ({
              id: kind ?? "disabled",
              kind: MdsDropdownItemKind.Button,
              label: kind ?? "disabled",
              isChecked: store.debug.operationsSubmitApiMockErrorKind === kind,
              onClick: () => store.debug.setOperationsSubmitApiMockErrorKind(kind),
            })),
          }}
        />
        {"|"}
        <MdsDropdownButton
          placement="below-right-alignment"
          label={store.debug.operationsSubmitApiMockErrorOperationKind ?? "all"}
          contentList={{
            items: operationKinds.map(kind => ({
              id: kind ?? "all",
              kind: MdsDropdownItemKind.Button,
              label: kind ?? "all",
              isChecked: store.debug.operationsSubmitApiMockErrorOperationKind === kind,
              onClick: () => store.debug.setOperationsSubmitApiMockErrorOperationKind(kind),
            })),
          }}
        />
      </div>
      <pre
        className={debugInfoStyles}
        onClick={e => e.altKey && setActionQueueExpanded(!actionQueueExpanded)}
      >
        {!actionQueueExpanded && "<Alt + click to expand action queue info>"}
        {actionQueueExpanded && JSON.stringify(store.sync.actionQueue.friendlyDebugInfo, null, 4)}
      </pre>
      <pre
        className={debugInfoStyles}
        onClick={e => e.altKey && setAudioCaptureExpanded(!audioCaptureExpanded)}
      >
        {!audioCaptureExpanded && "<Alt + click to expand audio capture info>"}
        {audioCaptureExpanded && JSON.stringify(store.audioCapture.friendlyDebugInfo, null, 4)}
      </pre>
      <br />
      <pre className={debugInfoStyles}>
        <span>Commit: {buildInfo.gitCommitSha}</span>
        <br />
        <span>
          Build at:{" "}
          {buildInfo.buildTimestamp.toLocal().toLocaleString(DateTime.DATETIME_FULL_WITH_SECONDS)}
        </span>
      </pre>
    </div>
  );
});

// Database view content
const ExpandedDatabaseContent = observer(function ExpandedDatabaseContent() {
  const { store } = useAppStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalSizeBytes, setTotalSizeBytes] = useState<number | null>(null);
  const [rowCounts, setRowCounts] = useState<Array<{ table: string; rowCount: number }>>([]);

  const isDbInitialized = store.database.isInitialized();

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const value = parseFloat((bytes / Math.pow(k, i)).toFixed(2));
    return `${value} ${sizes[i]}`;
  };

  const refresh = async () => {
    if (!isDbInitialized) return;
    setLoading(true);
    setError(null);
    try {
      const sizeBytes = await store.database.stats.getTotalSizeBytes();
      setTotalSizeBytes(sizeBytes);

      const counts = await store.database.stats.getRowCountsByTable();
      setRowCounts(counts);
    } catch (unknownErr) {
      const err = unknownErr as Error;
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDbInitialized]);

  if (!isDbInitialized) {
    return <div className={debugContentStyles}>Heads-up DB not initialized.</div>;
  }

  return (
    <div className={debugContentStyles}>
      <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
        <MDSButton variant={MDSButtonVariant.Transparent} onClick={refresh} isLoading={loading}>
          Refresh
        </MDSButton>
      </div>

      {error && <div style={{ color: mdsColors().red.x500 }}>Error: {error}</div>}

      <pre className={debugInfoStyles}>
        <div style={{ marginBottom: 8 }}>
          <strong>Total storage:</strong>{" "}
          {totalSizeBytes !== null ? formatBytes(totalSizeBytes) : "..."}
        </div>
        <div>
          <strong>Row counts (public):</strong>
        </div>
        <div>
          {rowCounts.length === 0 && "..."}
          {rowCounts.length > 0 && (
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {rowCounts.map(({ table, rowCount }) => (
                <li key={table} style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>{table}</span>
                  <span>{rowCount.toLocaleString()}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </pre>
    </div>
  );
});

/**
 * Debug content component for Heads Up sidebar state inspection
 */
const ExpandedHeadsUpContent = observer(function ExpandedHeadsUpContent() {
  const { store } = useAppStore();
  const spaceAccountId = store.spaceAccounts.myPersonalSpaceAccountId ?? null;
  const liveNoteId = store.routing.noteIdParam ?? null;

  const contextId = resolveSmartContextId({
    spaceAccountId,
    activeNoteId: liveNoteId ?? null,
    smartViewKind: SmartViewKind.HeadsUp,
  });

  const { context } = store.database.smartContexts.useById({
    contextId,
  });

  const smartContextId = context?.id ?? contextId;

  const { view } = store.database.smartViews.useLatestByContextId({
    smartContextId,
  });

  const { cards } = store.database.smartCards.useCardsBySmartViewId({
    smartViewId: view?.id ?? null,
  });

  const summary = {
    noteId: liveNoteId,
    contextId: smartContextId,
    contextPayload: context?.payload_json ?? null,
    viewId: view?.id ?? null,
    viewRefreshedAt: view?.refreshed_at ?? null,
    cardCount: cards.length,
  };

  return (
    <div className={debugContentStyles}>
      <pre className={debugInfoStyles}>{JSON.stringify(summary, null, 2)}</pre>
      <pre className={debugInfoStyles}>{JSON.stringify(cards, null, 2)}</pre>
    </div>
  );
});

/**
 * Debug content component for Voice/Audio capture features and upload controls.
 */
const ExpandedVoiceContent = observer(function ExpandedVoiceContent() {
  const { store } = useAppStore();

  return (
    <div className={debugContentStyles}>
      {/* Upload Controls */}
      <div
        style={{
          display: "flex",
          gap: 8,
          marginBottom: 16,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <MDSButton
          variant={MDSButtonVariant.Transparent}
          onClick={() => store.audioCapture.uploadManager?.pause()}
          isDisabled={!store.audioCapture.uploadManager}
        >
          Pause Upload
        </MDSButton>
        <MDSButton
          variant={MDSButtonVariant.Transparent}
          onClick={() => store.audioCapture.uploadManager?.resume()}
          isDisabled={!store.audioCapture.uploadManager}
        >
          Resume Upload
        </MDSButton>
        <MDSButton
          variant={MDSButtonVariant.Transparent}
          onClick={async () => {
            const status = {
              hasUploadManager: Boolean(store.audioCapture.uploadManager),
              uploadProgress: store.audioCapture.uploadProgressPercent,
              recordingStatus: store.audioCapture.status,
            };
            await copyTextToClipboard({
              text: JSON.stringify(status, null, 2),
            });
            toastModule.triggerToast({ content: "Upload status copied to clipboard" });
          }}
        >
          Copy Upload Status
        </MDSButton>
        {store.audioCapture.uploadManager && (
          <span style={{ fontSize: "12px", color: mdsColors().grey.x600 }}>
            Upload Manager Active
          </span>
        )}
      </div>

      {/* Audio Capture Debug Info */}
      <div style={{ marginBottom: 16 }}>
        <h4 style={{ margin: "0 0 8px 0", fontSize: "14px", fontWeight: 600 }}>
          Audio Capture Debug Info
        </h4>
        <pre className={scrollableDebugInfoStyles}>
          {JSON.stringify(store.audioCapture.friendlyDebugInfo, null, 2)}
        </pre>
      </div>

      {/* Transcriptions Debug Info */}
      <div style={{ marginBottom: 16 }}>
        <h4 style={{ margin: "0 0 8px 0", fontSize: "14px", fontWeight: 600 }}>
          Transcriptions Debug Info
        </h4>
        <pre className={scrollableDebugInfoStyles}>
          {JSON.stringify(store.audioCapture.interleavedTranscriptInfo, null, 2)}
        </pre>
      </div>
    </div>
  );
});

/**
 * This is a debugging overlay which displays various pieces of info about local application state.
 * Most notably, it displays the current sync action queue, and allows for copying the queue's debug info to the clipboard.
 */
export const DebugInfoOverlay = observer(function DebugInfoOverlay() {
  const { store } = useAppStore();

  if (!store.debug.debugModeEnabled) {
    return null;
  }

  const isOpen = store.debug.debugModeToolbarExpanded;
  const toggleOpen = () => store.debug.toggleDebugModeToolbar();

  return (
    <>
      {/* Overlay content when expanded */}
      {isOpen && (
        <div className={overlayContentStyles}>
          {store.debug.debugOverlayView === DebugOverlayViewKind.Standard && (
            <ExpandedDebugContent />
          )}
          {store.debug.debugOverlayView === DebugOverlayViewKind.Database && (
            <ExpandedDatabaseContent />
          )}
          {store.debug.debugOverlayView === DebugOverlayViewKind.Account && (
            <ExpandedFeatureFlagsContent />
          )}
          {store.debug.debugOverlayView === DebugOverlayViewKind.HeadsUp && (
            <ExpandedHeadsUpContent />
          )}
          {store.debug.debugOverlayView === DebugOverlayViewKind.Voice && <ExpandedVoiceContent />}
        </div>
      )}

      {/* Navigation bar - visible when expanded */}
      {isOpen && (
        <div className={navBarStyles}>
          <div className={navLeftStyles}>
            <MDSButton
              variant={MDSButtonVariant.Transparent}
              onClick={() => store.debug.setDebugOverlayView(DebugOverlayViewKind.Standard)}
            >
              Standard
            </MDSButton>
            <MDSButton
              variant={MDSButtonVariant.Transparent}
              onClick={() => store.debug.setDebugOverlayView(DebugOverlayViewKind.Database)}
            >
              DB
            </MDSButton>
            <MDSButton
              variant={MDSButtonVariant.Transparent}
              onClick={() => store.debug.setDebugOverlayView(DebugOverlayViewKind.HeadsUp)}
            >
              Heads Up
            </MDSButton>
            <MDSButton
              variant={MDSButtonVariant.Transparent}
              onClick={() => store.debug.setDebugOverlayView(DebugOverlayViewKind.Voice)}
            >
              Voice
            </MDSButton>
            <MDSButton
              variant={MDSButtonVariant.Transparent}
              onClick={() => store.debug.setDebugOverlayView(DebugOverlayViewKind.Account)}
            >
              Account
            </MDSButton>
          </div>
          <div className={navRightStyles}>
            <MDSButton
              variant={MDSButtonVariant.Transparent}
              onClick={() => store.sync.actionQueue.saveDebugInfoToFile()}
            >
              Save to file
            </MDSButton>
            <MDSButton
              variant={MDSButtonVariant.Transparent}
              onClick={async () => {
                const fullDebugInfo = store.sync.actionQueue.fullDebugInfo;
                await copyTextToClipboard({
                  text: JSON.stringify(fullDebugInfo, null, 4),
                });
                toastModule.triggerToast({ content: "Copied queue debug info to clipboard." });
              }}
            >
              Copy Info
            </MDSButton>
          </div>
        </div>
      )}

      {/* Bottom bar - always visible */}
      <div className={bottomBarStyles(isOpen)}>
        <div className={headerStyles}>
          {!isOpen && (
            <>
              <PerformanceMetrics />
              <MinimizeButton onClick={toggleOpen} variant={MDSButtonVariant.Transparent}>
                Expand
              </MinimizeButton>
            </>
          )}
          {isOpen && (
            <>
              <PerformanceMetrics />
              <div className={headerButtonGroupStyles}>
                <MinimizeButton onClick={toggleOpen} variant={MDSButtonVariant.Transparent}>
                  Minimize
                </MinimizeButton>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
});

const MinimizeButton = styled(MDSButton)`
  font-size: ${({ theme }) => theme.fontSizes.xxsmall};
  padding: 2px 4px;
`;

// Updated styles
const bottomBarStyles = (isOpen: boolean) =>
  css({
    backgroundColor: mdsColors().white,
    border: `1px solid ${mdsColors().grey.x300}`,
    borderRadius: "4px",
    bottom: 0,
    padding: "4px 8px",
    position: "fixed",
    right: 0,
    fontSize: "14px",
    width: isOpen ? "600px" : "auto",
    height: "34px",
    zIndex: ZIndex.DebugOverlay,
    opacity: isOpen ? 1 : 0.8,
    transition: "all 0.2s ease",
  });

const overlayContentStyles = css({
  backgroundColor: mdsColors().white,
  border: `1px solid ${mdsColors().grey.x300}`,
  borderRadius: "4px",
  bottom: "80px", // Position above nav bar (34px) and bottom bar (34px)
  padding: "16px",
  position: "fixed",
  right: 0,
  fontSize: "14px",
  width: "600px",
  maxHeight: "calc(100vh - 100px)",
  overflow: "auto",
  zIndex: ZIndex.DebugOverlay - 1,
  boxShadow: "0 -2px 8px rgba(0, 0, 0, 0.1)",
});

const navBarStyles = css({
  backgroundColor: mdsColors().white,
  border: `1px solid ${mdsColors().grey.x300}`,
  borderRadius: "4px",
  bottom: "42px", // Just above the bottom bar
  padding: "4px 8px",
  position: "fixed",
  right: 0,
  fontSize: "14px",
  width: "600px",
  zIndex: ZIndex.DebugOverlay,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
});

const navLeftStyles = css({
  display: "flex",
  gap: "4px",
  alignItems: "center",
});

const navRightStyles = css({
  display: "flex",
  gap: "4px",
  alignItems: "center",
});

const headerStyles = css({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  height: "100%",
  gap: "16px",
});

const headerButtonGroupStyles = css({
  display: "flex",
  alignItems: "center",
  gap: "4px",
});

const performanceMetricsStyles = css({
  display: "flex",
  gap: "12px",
  fontSize: "12px",
  fontFamily: "monospace",
  color: mdsColors().grey.x600,
  alignItems: "center",
  "& > span": {
    whiteSpace: "nowrap",
    "&:nth-of-type(1)": {
      width: "60px", // FPS: XX
    },
    "&:nth-of-type(2)": {
      width: "80px", // Frame: XXms
    },
    "&:nth-of-type(3)": {
      width: "80px", // Mem: XXXmb
    },
  },
});

const debugContentStyles = css({
  display: "flex",
  flexDirection: "column",
});

const mockApiErrorRowStyles = css({
  alignItems: "center",
  display: "flex",
  gap: "8px",
  marginBottom: "16px",
});

const debugInfoStyles = css({
  fontSize: "12px",
  overflow: "auto",
  maxHeight: "300px",
  backgroundColor: mdsColors().grey.x100,
  padding: "12px",
  borderRadius: "4px",
  fontFamily: "monospace",
});

const scrollableDebugInfoStyles = css({
  fontSize: "12px",
  overflow: "auto",
  maxHeight: "200px",
  backgroundColor: mdsColors().grey.x100,
  padding: "12px",
  borderRadius: "4px",
  fontFamily: "monospace",
  margin: 0,
  border: `1px solid ${mdsColors().grey.x200}`,
});

// Feature flags view content
const ExpandedFeatureFlagsContent = observer(function ExpandedFeatureFlagsContent() {
  const { store } = useAppStore();
  const flags = store.spaceAccountFeatureFlags.featureFlags;
  const entries = Object.entries(flags).sort(([a], [b]) => a.localeCompare(b));

  return (
    <div className={debugContentStyles}>
      <pre className={debugInfoStyles}>
        {entries.length === 0 ? (
          <div>No feature flags loaded.</div>
        ) : (
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {entries.map(([key, value]) => {
              const isOn = Boolean(value);
              const liStyle: React.CSSProperties = {
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "4px 8px",
                borderRadius: 4,
                marginBottom: 4,
                backgroundColor: isOn ? mdsColors().green.x700 : mdsColors().grey.x200,
              };
              const statusStyle: React.CSSProperties = {
                fontWeight: 600,
                color: isOn ? mdsColors().green.x400 : mdsColors().grey.x800,
                textTransform: "uppercase",
                fontSize: 11,
              };
              return (
                <li key={key} style={liStyle}>
                  <span>{key}</span>
                  <span style={statusStyle}>{isOn ? "ON" : "OFF"}</span>
                </li>
              );
            })}
          </ul>
        )}
      </pre>
    </div>
  );
});
